import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Calendar, ArrowRight, User, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { jobsAPI } from '../lib/api';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';

// Categorias permitidas nesta página (Informativas)
const NEWS_CATEGORIES = ['noticia', 'eleicao', 'concurso', 'curso'];

const CATEGORY_STYLES = {
  noticia: { label: 'Notícia', color: 'bg-cyan-100 text-cyan-700 border-cyan-200 dark:bg-cyan-500/10 dark:text-cyan-400 dark:border-cyan-500/20' },
  eleicao: { label: 'Eleição / Edital', color: 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-500/10 dark:text-yellow-400 dark:border-yellow-500/20' },
  concurso: { label: 'Concurso', color: 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-500/10 dark:text-purple-400 dark:border-purple-500/20' },
  curso: { label: 'Curso', color: 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-500/10 dark:text-orange-400 dark:border-orange-500/20' }
};

const NewsCard = ({ item }) => {
  const style = CATEGORY_STYLES[item.category] || CATEGORY_STYLES.noticia;
  
  // Define o link usando Slug se existir, caso contrário usa o ID
  const newsPath = item.slug || item.id;

  // Limpa o HTML da descrição e cria um resumo elegante
  const plainText = item.description ? item.description.replace(/<[^>]*>?/gm, '') : '';
  const summary = plainText.length > 120 ? plainText.substring(0, 120) + '...' : plainText;

  return (
    <Link to={`/noticias/${newsPath}`} className="group flex flex-col bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 h-full">
      {/* Imagem de Capa */}
      <div className="h-48 overflow-hidden bg-slate-100 dark:bg-slate-800 relative">
        {item.image_url ? (
          <img 
            src={item.image_url} 
            alt={item.title} 
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-300 dark:text-slate-700">
            <Tag className="w-12 h-12" />
          </div>
        )}
        <div className="absolute top-3 left-3">
            <Badge className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border shadow-sm ${style.color}`}>
                {style.label}
            </Badge>
        </div>
      </div>

      {/* Conteúdo */}
      <div className="p-6 flex-1 flex flex-col">
        <div className="flex items-center gap-2 text-xs text-slate-400 mb-3 font-medium">
            <Calendar className="w-3.5 h-3.5" />
            {formatDate(item.created_at)}
        </div>

        <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3 line-clamp-2 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors leading-tight">
            {item.title}
        </h3>

        <p className="text-slate-500 dark:text-slate-400 text-sm line-clamp-3 mb-4 flex-1 leading-relaxed">
            {summary}
        </p>

        <div className="flex items-center justify-between pt-4 border-t border-slate-100 dark:border-slate-800/50 mt-auto">
            <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 font-medium">
                <div className="w-6 h-6 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                    <User className="w-3 h-3 text-teal-600 dark:text-teal-400" />
                </div>
                <span className="truncate max-w-[120px]">{item.company_name}</span>
            </div>
            <span className="text-teal-600 dark:text-teal-400 text-xs font-bold flex items-center group-hover:translate-x-1 transition-transform">
                Ler Mais <ArrowRight className="w-3 h-3 ml-1" />
            </span>
        </div>
      </div>
    </Link>
  );
};

const NewsPage = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const loadNews = async () => {
      try {
        setLoading(true);
        // Busca os dados da API
        const res = await jobsAPI.getAll({ limit: 100 });
        
        // Filtra para mostrar apenas categorias informativas/notícias
        const filteredNews = res.data.filter(item => NEWS_CATEGORIES.includes(item.category));
        setNews(filteredNews);
      } catch (err) {
        console.error("Erro ao carregar notícias:", err);
        toast.error('Não foi possível carregar as notícias.');
      } finally {
        setLoading(false);
      }
    };
    loadNews();
  }, []);

  // Filtro de busca em tempo real (Título ou Nome da Empresa/Fonte)
  const displayedNews = news.filter(item => 
    item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.company_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#020617] py-12 transition-colors duration-300">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        
        {/* Header da Página */}
        <div className="text-center mb-12 space-y-4">
          <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 dark:text-white tracking-tight">
            Últimas Notícias & <span className="text-teal-600 dark:text-teal-400">Editais</span>
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Acompanhe concursos, cursos, processos seletivos e as principais novidades do mercado de Rondônia.
          </p>
        </div>

        {/* Barra de Busca Centralizada */}
        <div className="max-w-xl mx-auto mb-16 relative group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 group-focus-within:text-teal-500 transition-colors" />
            <Input 
                placeholder="Buscar notícias ou editais..." 
                className="pl-12 py-7 rounded-2xl shadow-xl shadow-slate-200/50 dark:shadow-none border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-lg focus:ring-teal-500/20"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
            />
        </div>

        {/* Grid de Resultados */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {loading ? (
             // Skeleton Loading
             [...Array(6)].map((_, i) => (
                <div key={i} className="h-[420px] bg-white dark:bg-slate-900/50 border border-slate-100 dark:border-slate-800 rounded-2xl animate-pulse" />
             ))
          ) : displayedNews.length > 0 ? (
            displayedNews.map(item => <NewsCard key={item.id} item={item} />)
          ) : (
            // Estado Vazio
            <div className="col-span-full text-center py-24 bg-white dark:bg-slate-900/40 rounded-3xl border border-dashed border-slate-200 dark:border-slate-800">
                <div className="bg-slate-50 dark:bg-slate-800 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Search className="w-10 h-10 text-slate-300 dark:text-slate-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-700 dark:text-white mb-2">Nenhuma notícia encontrada</h3>
                <p className="text-slate-500 dark:text-slate-400">Tente buscar por termos diferentes ou limpe sua pesquisa.</p>
                <Button 
                    variant="link" 
                    onClick={() => setSearchTerm('')}
                    className="mt-4 text-teal-600 hover:text-teal-700 font-bold"
                >
                    Ver todas as notícias
                </Button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default NewsPage;